/**
 * Media URL Helper — Phase 6 Critical: Strict Cloudinary-only mode.
 *
 * Records with bare filenames or legacy /api/files/ / /uploads/ paths are
 * treated as INVALID and surface a branded RGM placeholder instead of an
 * Unsplash silent fallback. This forces broken records to be visible (and
 * thus re-uploaded) rather than masked behind a generic stock photo.
 */

const BACKEND_URL = window.location.origin; // eslint-disable-line no-unused-vars

// Branded placeholder (inline SVG — no extra network round-trip, no flash)
const RGM_COVER_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#1a0f2e"/>
      <stop offset="50%" stop-color="#2d1a4e"/>
      <stop offset="100%" stop-color="#0a0a0f"/>
    </linearGradient>
    <radialGradient id="r" cx="0.7" cy="0.3" r="0.7">
      <stop offset="0%" stop-color="#a855f7" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#a855f7" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="400" height="400" fill="url(#g)"/>
  <rect width="400" height="400" fill="url(#r)"/>
  <g transform="translate(200,180)" text-anchor="middle">
    <text font-family="Arial, sans-serif" font-size="40" font-weight="900" fill="#ffffff" letter-spacing="2">RGM</text>
    <text y="32" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="rgba(255,255,255,0.6)" letter-spacing="3">STUDIO</text>
    <text y="76" font-family="Arial, sans-serif" font-size="11" fill="rgba(255,255,255,0.4)">cover unavailable</text>
  </g>
</svg>`
  );

const RGM_AVATAR_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#a855f7"/>
      <stop offset="100%" stop-color="#ec4899"/>
    </linearGradient>
  </defs>
  <rect width="200" height="200" rx="100" fill="url(#g)"/>
  <text x="100" y="118" font-family="Arial, sans-serif" font-size="64" font-weight="900" fill="#ffffff" text-anchor="middle">R</text>
</svg>`
  );

const RGM_BANNER_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="400" viewBox="0 0 1200 400">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#1a0f2e"/>
      <stop offset="100%" stop-color="#0a0a0f"/>
    </linearGradient>
  </defs>
  <rect width="1200" height="400" fill="url(#g)"/>
</svg>`
  );

// Cloudinary delivery widths (2x of on-screen size, capped). Keep this list
// small — every width is a separately cached derived asset.
export const IMAGE_SIZES = { avatar: 192, thumb: 400, card: 800, cover: 1600 };

const _TRANSFORM_SEG = /^[a-z]{1,3}_[^/]+$/;

// Adds f_auto,q_auto,c_limit,w_N to Cloudinary image delivery URLs. Non-Cloudinary,
// video/raw, and already-transformed URLs are returned unchanged.
export const optimizeImage = (url, size = 'card') => {
  if (!url || typeof url !== 'string') return url;
  const marker = 'res.cloudinary.com/';
  if (!url.includes(marker) || !url.includes('/image/upload/')) return url;
  const idx = url.indexOf('/image/upload/') + '/image/upload/'.length;
  const firstSeg = url.slice(idx).split('/')[0];
  if (firstSeg.split(',').every((p) => _TRANSFORM_SEG.test(p))) return url;
  const w = IMAGE_SIZES[size] || IMAGE_SIZES.card;
  return `${url.slice(0, idx)}f_auto,q_auto,c_limit,w_${w}/${url.slice(idx)}`;
};

/**
 * Returns true when the URL is a "real" remote URL (Cloudinary or any external
 * https:// host) — but NOT a local /api/files/, /uploads/, or bare filename.
 */
const _isValidRemoteUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  if (url.startsWith('data:')) return true;            // inline data-uri OK
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // External preview URLs route to ephemeral filesystem — flag invalid
    if (url.includes('emergentagent.com/api/files/')) return false;
    if (url.includes('emergentagent.com/api/public/')) return false;
    return true;
  }
  return false;
};

/**
 * Get the URL for a music file. Strict — bare filenames return ''.
 */
export const getMusicUrl = (submission) => {
  if (!submission) return '';
  if (_isValidRemoteUrl(submission.music_url)) return submission.music_url;
  return '';
};

/**
 * Get the URL for cover art — strict Cloudinary-only with cache-busting.
 * Returns the branded RGM placeholder when no valid remote URL exists.
 */
export const getCoverUrl = (submission, size = 'card') => {
  if (!submission) return RGM_COVER_PLACEHOLDER;

  if (_isValidRemoteUrl(submission.cover_url)) {
    return appendCacheBust(optimizeImage(submission.cover_url, size), submission.cover_updated_at);
  }
  // Some collections (e.g. playlists pasted in as { cover_art: cdn_url }) put
  // a full URL in cover_art — accept those too, otherwise treat as invalid.
  if (_isValidRemoteUrl(submission.cover_art)) {
    return appendCacheBust(optimizeImage(submission.cover_art, size), submission.cover_updated_at);
  }
  return RGM_COVER_PLACEHOLDER;
};

/**
 * Get the URL for a playlist cover image — strict Cloudinary-only.
 */
export const getPlaylistCoverUrl = (playlist, size = 'card') => {
  if (!playlist) return RGM_COVER_PLACEHOLDER;
  if (_isValidRemoteUrl(playlist.cover_image)) {
    return appendCacheBust(optimizeImage(playlist.cover_image, size), playlist.cover_updated_at || playlist.updated_at);
  }
  return RGM_COVER_PLACEHOLDER;
};

/**
 * Append a cache-busting query param to defeat stale browser caches after
 * a cover re-upload.
 */
const appendCacheBust = (url, version) => {
  if (!url || !version) return url;
  if (url.startsWith('data:')) return url; // never cache-bust data URIs
  const sep = url.includes('?') ? '&' : '?';
  const v = encodeURIComponent(String(version));
  return `${url}${sep}v=${v}`;
};

/**
 * Get the URL for a profile photo — strict mode. Accepts external Google
 * (`picture`) URLs, Cloudinary uploads, or returns the branded R avatar.
 */
export const getProfilePhotoUrl = (user, size = 'avatar') => {
  if (!user) return RGM_AVATAR_PLACEHOLDER;
  if (_isValidRemoteUrl(user.profile_photo)) {
    return appendCacheBust(optimizeImage(user.profile_photo, size), user.profile_photo_updated_at);
  }
  // Google OAuth profile picture is fine
  if (_isValidRemoteUrl(user.picture)) return optimizeImage(user.picture, size);
  return RGM_AVATAR_PLACEHOLDER;
};

/**
 * Get the URL for a banner image — strict mode.
 */
export const getBannerUrl = (user, size = 'cover') => {
  if (!user) return RGM_BANNER_PLACEHOLDER;
  if (_isValidRemoteUrl(user.banner_image)) {
    return appendCacheBust(optimizeImage(user.banner_image, size), user.banner_image_updated_at);
  }
  return RGM_BANNER_PLACEHOLDER;
};

export const getDefaultAvatar = () => RGM_AVATAR_PLACEHOLDER;
export const getDefaultBanner = () => RGM_BANNER_PLACEHOLDER;
export const getDefaultCover = () => RGM_COVER_PLACEHOLDER;

/**
 * Check if a URL is a Cloudinary URL
 */
export const isCloudinaryUrl = (url) => {
  return !!(url && typeof url === 'string' && url.includes('res.cloudinary.com'));
};

/**
 * Check if a record's media is "broken" (bare filename / local / missing).
 * Used by admins / broken-asset banners to surface re-upload prompts.
 */
export const isBrokenCover = (submission) =>
  !!submission &&
  !_isValidRemoteUrl(submission.cover_url) &&
  !_isValidRemoteUrl(submission.cover_art);

/**
 * Phase 21.3a5 — Centralized share-cover resolver. Every share surface
 * (TrackPage share, Dashboard share, Featured recap, native share,
 * community share, Copy Link) MUST resolve the same cover URL. Prior to
 * this helper each surface checked a different subset of fields and
 * some silently fell back to a blank placeholder even when a valid
 * cover existed on the record. Accepts every known variant.
 */
export const resolveShareCover = (record) => {
  if (!record) return RGM_COVER_PLACEHOLDER;
  const v = record.cover_updated_at || record.updated_at;
  const candidates = [
    record.cover_url,
    record.cover_image,
    record.cover_art,
    record.cover_art_url,
    record.coverUrl,
    record.coverArt,
    record.coverImage,
    record.track_cover,
    record.trackCover,
    record.image_url,
    record.imageUrl,
  ];
  for (const c of candidates) {
    if (_isValidRemoteUrl(c)) return appendCacheBust(c, v);
  }
  return RGM_COVER_PLACEHOLDER;
};

/**
 * Phase 21.3a5 — Centralized artist-photo resolver for share visuals.
 * Same rationale as resolveShareCover — some share renderers checked
 * only `artist.profile_photo` while backend payloads sometimes only
 * populate `artist.avatar_url` or `user.picture`.
 */
export const resolveShareArtistPhoto = (record) => {
  if (!record) return RGM_AVATAR_PLACEHOLDER;
  const v = record.profile_photo_updated_at || record.updated_at;
  const candidates = [
    record.profile_photo,
    record.avatar_url,
    record.artist_avatar,
    record.artistAvatar,
    record.host_avatar,
    record.hostAvatar,
    record.picture,
    record.avatar,
  ];
  for (const c of candidates) {
    if (_isValidRemoteUrl(c)) return appendCacheBust(c, v);
  }
  return RGM_AVATAR_PLACEHOLDER;
};

export default {
  getMusicUrl,
  getCoverUrl,
  getPlaylistCoverUrl,
  getProfilePhotoUrl,
  getBannerUrl,
  getDefaultAvatar,
  getDefaultBanner,
  getDefaultCover,
  isCloudinaryUrl,
  isBrokenCover,
  optimizeImage,
  resolveShareCover,
  resolveShareArtistPhoto,
};
